import { useToken } from "@gluestack-style/react";
import { SiteSelect } from "components/form/SiteSelect";
import Container from "components/general/Container";
import PageHeader from "components/general/Headers/PageHeader";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import Seo from "components/general/Seo";
import AbcoMap from "components/map";
import Pagination from "components/navigation/Pagination";
import SiteItem from "components/sites/SiteItem";
import { Box, Button, CloseIcon, H3, HStack, Input, InputField, InputIcon, InputSlot, Link, Text, Typography } from "components/ui";
import { ArrowDown, ArrowUp, ListTree, Map } from "lucide-react-native";
import React, { useEffect } from "react";
import { ActivityIndicator, Pressable } from "react-native";
import { Col, Container as GridContainer, Row } from "react-native-flex-grid";
import { useGetSitesQuery } from "store/abcoApi";
import { Value } from "types/select.types";
import { useIsMobile } from "utils/hooks/useIsMobile";

export default function Page() {
	const [pageNumber, setPageNumber] = React.useState(1);
	const [searchTerm, setSearchTerm] = React.useState("");
	const [sort, setSort] = React.useState("Name");
	const [direction, setDirection] = React.useState("asc");
	const [isMapView, setIsMapView] = React.useState(false);
	const [site, setSite] = React.useState<Value[]>([]);
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const isMobile = useIsMobile();
	const { data, isLoading, isFetching, isError, error, currentData } = useGetSitesQuery({
		pageNumber,
		searchTerm: searchTerm == "" ? undefined : searchTerm,
		// @ts-ignore
		sortField: sort,
		sortDirection: direction,
		pageSize: isMapView ? 9999 : 10,
		groupIdCollection: site.map((s) => s.id),
	});

	const [siteData, setSiteData] = React.useState(data);

	useEffect(() => {
		if (data) {
			setSiteData(data);
		}
	}, [data]);
	if (isFetching && isLoading) {
		return (
			<Box height={"$full"} justifyContent="center" alignItems="center" bg="transparent">
				<Box flex={1} justifyContent="center">
					<ActivityIndicator size="large" style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }] }} />
				</Box>
			</Box>
		);
	}
	if (siteData == null || siteData.list == null) {
		return;
	}

	const setSortDirection = (sort: string) => {
		if (sort === sort) {
			setDirection(direction === "asc" ? "desc" : "asc");
		}

		setSort(sort);
	};
	const SortButton = ({ title, sortKey }: { title: string; sortKey: string }) => {
		return (
			<Button gap={8} justifyContent="flex-start" flexDirection="row" width={"$full"} onPress={() => setSortDirection(sortKey)}>
				<Text>{title}</Text>
				{sort === sortKey && direction === "asc" && <ArrowDown width={18} height={18} />}
				{sort === sortKey && direction === "desc" && <ArrowUp width={18} height={18} />}
			</Button>
		);
	};
	const dummyOptions = [
		{ value: "1", label: "Option 1" },
		{ value: "2", label: "Option 2" },
		{ value: "3", label: "Option 3" },
	];

	if (isFetching && isLoading) {
		return (
			<Box minHeight={"$full"} justifyContent="center" borderRadius={16}>
				<ActivityIndicator size={"large"} style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }, { translateY: 25 }] }} />
			</Box>
		);
	}
	return (
		<ScrollContainer>
			<Seo title="My Sites | Abconet" description="Manage your assigned sites, including site-specific configurations, assets, and activities." />
			<Container>
				<PageHeader title={"My Sites"} />
				<Section paddingHorizontal={isMobile ? 12 : 47} padding={0} backgroundColor="$basicwhite">
					<GridContainer
						noPadding
						style={{
							marginLeft: 0,
							maxWidth: "100%",
							marginBottom: 30,
							zIndex: 9,
						}}
					>
						<Row style={{ gap: isMobile ? 48 : 0 }}>
							<Col style={{ position: "relative", zIndex: 99 }} xs={12} md={4}>
								<Box zIndex={2}>
									<Typography
										sx={{
											top: 0,
											position: "absolute",
											zIndex: 1,
											lineHeight: 1.5,
											left: 11,
											background: "white",
											paddingLeft: 6,
											fontSize: 13,
											fontWeight: 700,
											paddingRight: 6,
										}}
									>
										Filter by keywords:
									</Typography>
									<Input flex={1} px="$0" borderWidth={1} borderColor="$neutral300" borderRadius={12} py="$3" isDisabled={false} isInvalid={false} isReadOnly={false} isFocused={true} isHovered={true}>
										<InputField borderWidth={0} fontSize={16} borderRadius={"$lg"} placeholder={"Type here..."} value={searchTerm} onChangeText={setSearchTerm} color="$basicblack" placeholderTextColor={"#cccccc"} />
										<InputSlot>
											{searchTerm.length > 0 && (
												<Pressable
													onPress={() => {
														setSearchTerm("");
													}}
												>
													<InputIcon as={CloseIcon} mr="$4" color={"$textLight400"} />
												</Pressable>
											)}
										</InputSlot>
									</Input>
								</Box>
							</Col>

							<Col xs={12} md={4}>
								<SiteSelect
									onSelect={(value) => {
										setSite(value);
									}}
									value={site}
									isMulti={true}
									allowedTypes="Both"
									matchMode="Group"
									title="Filter by site/group"
								/>
							</Col>
						</Row>
					</GridContainer>
				</Section>
				<Section>
					{isLoading && <Text>Loading...</Text>}
					<HStack marginBottom={30} gap={8} justifyContent="flex-end">
						{!isMobile && !isMapView && siteData?.list && siteData.list.length > 0 && (
							<Button
								gap={8}
								alignItems="center"
								onPress={() => {
									setIsMapView(true);
								}}
							>
								<Map size={24} color={resolvedPrimarylight} />
								<Box borderBottomColor="$primarylight" borderBottomWidth={1}>
									<Text color="$primarylight">View as map</Text>
								</Box>
							</Button>
						)}
						{isMapView && siteData?.list && siteData.list.length > 0 && (
							<Button
								gap={8}
								alignItems="center"
								onPress={() => {
									setIsMapView(false);
								}}
							>
								<ListTree size={24} color={resolvedPrimarylight} />
								<Box borderBottomColor="$primarylight" borderBottomWidth={1}>
									<Text color="$primarylight">View as list</Text>
								</Box>
							</Button>
						)}
					</HStack>

					<GridContainer
						noPadding
						style={{
							marginLeft: 0,
							width: "100%",
							maxWidth: "100%",
						}}
					>
						{isMapView && siteData?.list && siteData.list.length > 0 && <AbcoMap data={siteData?.list} />}

						{!isMapView && siteData?.list && siteData.list.length > 0 && (
							<Row style={{ marginLeft: 0, marginRight: 0, marginBottom: 14, width: "100%", paddingHorizontal: 16 }}>
								<Col style={{ justifyContent: "center" }} xs={`4`} md={2}>
									<SortButton title="Group" sortKey="GroupName" />
								</Col>
								<Col xs={4} md={4}>
									<SortButton title="Site Name" sortKey="Name" />
								</Col>
								<Col style={{ justifyContent: "center" }} xs={3} md={3}>
									<SortButton title="Suburb" sortKey="Suburb" />
								</Col>
							</Row>
						)}

						{!isMapView && siteData?.list && siteData.list.length === 0 && (
							<Section gap={24}>
								<Box flex={2} gap={12}>
									<H3>No sites found</H3>
									<Typography sx={{ lineHeight: 21 }}>This page keeps track of all your sites — acting as your team's site register.</Typography>
									<Typography sx={{ lineHeight: 21 }}>Right now, it's empty — once sites are added, everything will appear here automatically.</Typography>
								</Box>
								<Box flex={1} width={"fit-content"}>
									<Button isLink borderRadius={8} variant="primary-light" href="/admin/groups">
										<Text sx={{ color: "$basicwhite" }}>Add New Site</Text>
									</Button>
								</Box>
							</Section>
						)}

						{!isMapView &&
							siteData?.list &&
							siteData.list.length > 0 &&
							siteData.list.map((site: any) => (
								<Link
									href={`/sites/${site.id}`}
									key={site.id}
									component={(hovered: boolean) => {
										var styleData = { marginLeft: 0, marginRight: 0, paddingHorizontal: 16, paddingVertical: 18, borderRadius: 8, borderWidth: 1, marginBottom: 14, borderColor: "#E1E9F0", backgroundColor: "white" };

										if (hovered) {
											// @ts-ignore
											styleData = { ...styleData, backgroundColor: "#E1E9F0" };
										}
										return <SiteItem data={site} styleData={styleData} />;
									}}
								></Link>
							))}
					</GridContainer>
					<Box justifyContent="center" marginTop={30} alignItems="center">
						<Pagination pageCount={siteData?.metaData?.pageCount} pageNumber={siteData?.metaData?.pageNumber} onChangePage={setPageNumber} />
					</Box>
				</Section>
			</Container>
		</ScrollContainer>
	);
}
